/**
 * 预定会议：日期/时长/静音预填与提交换算。
 */
/** 时长选项（分钟）。 */
export const SCHEDULE_DURATION_MINUTES = [15, 30, 45, 60, 90, 120, 180];
/** 默认时长。 */
export const SCHEDULE_DEFAULT_DURATION_MINUTES = 30;
/** 时间下拉步长（分钟）。 */
export const SCHEDULE_TIME_STEP_MINUTES = 15;
/** 手机滚轮分钟步长。 */
export const SCHEDULE_WHEEL_MINUTE_STEP = 5;
/** 手机日期滚轮可往后拉的天数（今天起跨年连续，不展示年份）。 */
export const SCHEDULE_DATE_HORIZON_DAYS = 1095;
/** 滚轮小时 0–23。 */
export const SCHEDULE_WHEEL_HOURS = Array.from({ length: 24 }, (_, hour) => hour);
/** 滚轮分钟（5 分钟一档）。 */
export const SCHEDULE_WHEEL_MINUTES = Array.from({ length: 60 / SCHEDULE_WHEEL_MINUTE_STEP }, (_, index) => index * SCHEDULE_WHEEL_MINUTE_STEP);
/** 入会静音策略。 */
export const SCHEDULE_MUTE = {
    AFTER_6: 'after6',
    OFF: 'off',
    ON: 'on'
};
const WEEKDAY_LABELS = ['日', '一', '二', '三', '四', '五', '六'];
const WEEKDAY_FULL_LABELS = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
/**
 * 补零。
 */
function pad2(value) {
    return String(value).padStart(2, '0');
}
/**
 * 本地日期 yyyy-MM-dd。
 */
export function toDateInputValue(date) {
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}
/**
 * 本地时间 HH:mm。
 */
export function toTimeInputValue(date) {
    return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}
/**
 * 当前时间向上取整到下一个 30 分钟。
 */
export function getDefaultScheduleStart() {
    const rounded = new Date();
    rounded.setSeconds(0, 0);
    rounded.setMilliseconds(0);
    const minutes = rounded.getMinutes();
    if (minutes === 0 || minutes === 30) {
        rounded.setMinutes(minutes + 30);
    }
    else if (minutes < 30) {
        rounded.setMinutes(30);
    }
    else {
        rounded.setHours(rounded.getHours() + 1);
        rounded.setMinutes(0);
    }
    return {
        date: toDateInputValue(rounded),
        time: toTimeInputValue(rounded)
    };
}
/**
 * 日期展示：2026/8/13 周四（中文）或 2026/8/13 Thu（英文）。
 */
export function formatDateWithWeekday(dateValue, lang = 'zh-CN') {
    const [year, month, day] = dateValue.split('-').map(Number);
    if (!year || !month || !day) {
        return dateValue;
    }
    const date = new Date(year, month - 1, day);
    if (lang.startsWith('zh')) {
        const week = WEEKDAY_LABELS[date.getDay()] || '';
        return `${year}/${month}/${day} 周${week}`;
    }
    const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
    return `${year}/${month}/${day} ${weekday}`;
}
/**
 * 15 分钟一档的时间选项。
 */
export function buildTimeOptions() {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += SCHEDULE_TIME_STEP_MINUTES) {
            options.push(`${pad2(hour)}:${pad2(minute)}`);
        }
    }
    return options;
}
/**
 * 时长文案。
 */
export function formatDurationLabel(minutes) {
    const hours = Math.floor(minutes / 60);
    const remain = minutes % 60;
    if (hours <= 0) {
        return `${remain}分钟`;
    }
    if (remain <= 0) {
        return `${hours}小时`;
    }
    return `${hours}小时${remain}分钟`;
}
/**
 * 手机表单开始时间：08月14日 10:00（不显示年份）。
 */
export function formatMobileStartLabel(dateValue, timeValue) {
    const { day, month } = parseDateParts(dateValue);
    return `${pad2(month)}月${pad2(day)}日 ${timeValue}`;
}
/**
 * 日期滚轮文案：今天 / 8月15日 周六（跨年也不带年份）。
 */
export function formatWheelDateLabel(dateValue, todayValue) {
    if (dateValue === todayValue) {
        return '今天';
    }
    const { day, month, year } = parseDateParts(dateValue);
    const date = new Date(year, month - 1, day);
    const week = WEEKDAY_FULL_LABELS[date.getDay()] || '';
    return `${month}月${day}日 ${week}`;
}
/**
 * 从今天起连续生成日期（yyyy-MM-dd），可跨年到次年 1 月、2 月。
 */
export function buildFutureDateValues(days = SCHEDULE_DATE_HORIZON_DAYS) {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const values = [];
    for (let index = 0; index < days; index++) {
        const date = new Date(start);
        date.setDate(start.getDate() + index);
        values.push(toDateInputValue(date));
    }
    return values;
}
/**
 * 拆分时长为小时+分钟。
 */
export function splitDurationMinutes(totalMinutes) {
    const safe = Math.max(0, totalMinutes);
    return {
        hours: Math.floor(safe / 60),
        minutes: safe % 60
    };
}
/**
 * 小时+分钟合成总分钟；至少 5 分钟。
 */
export function combineDurationMinutes(hours, minutes) {
    return Math.max(SCHEDULE_WHEEL_MINUTE_STEP, (hours * 60) + minutes);
}
/**
 * 将分钟对齐到滚轮步长。
 */
export function alignWheelMinutes(minutes) {
    const rounded = Math.round(minutes / SCHEDULE_WHEEL_MINUTE_STEP) * SCHEDULE_WHEEL_MINUTE_STEP;
    if (rounded >= 60) {
        return 55;
    }
    return Math.max(0, rounded);
}
/**
 * 默认会议主题。
 */
export function buildDefaultScheduleTitle(displayName, lang = 'zh-CN') {
    const name = (displayName || '').trim();
    if (lang.startsWith('zh')) {
        return name ? `${name}预定的会议` : '预定的会议';
    }
    return name ? `${name}'s scheduled meeting` : 'Scheduled meeting';
}
/**
 * 本地日期+时间转 ISO。
 */
export function toScheduledIso(dateValue, timeValue) {
    const [year, month, day] = dateValue.split('-').map(Number);
    const [hour, minute] = timeValue.split(':').map(Number);
    return new Date(year, month - 1, day, hour || 0, minute || 0, 0, 0).toISOString();
}
/**
 * 开始时间加分钟得到结束 ISO。
 */
export function addMinutesIso(startIso, minutes) {
    return new Date(new Date(startIso).getTime() + (minutes * 60 * 1000)).toISOString();
}
/**
 * 静音策略映射到 Jitsi config.startAudioMuted；关闭则不传。
 */
export function toStartAudioMuted(mode) {
    if (mode === SCHEDULE_MUTE.ON) {
        return 1;
    }
    if (mode === SCHEDULE_MUTE.AFTER_6) {
        return 6;
    }
    return undefined;
}
/** 日历星期表头（周日开始，对齐 Date#getDay）。 */
export const CALENDAR_WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六'];
/**
 * 解析 yyyy-MM-dd。
 */
export function parseDateParts(dateValue) {
    const [year, month, day] = dateValue.split('-').map(Number);
    return {
        day: day || 1,
        month: month || 1,
        year: year || new Date().getFullYear()
    };
}
/**
 * 月份加减。
 */
export function addCalendarMonths(year, month, delta) {
    const date = new Date(year, month - 1 + delta, 1);
    return {
        month: date.getMonth() + 1,
        year: date.getFullYear()
    };
}
/**
 * 日历标题：2026年8月。
 */
export function formatCalendarTitle(year, month) {
    return `${year}年${month}月`;
}
/**
 * 生成 6x7 日历格子（含上/下月填充）。
 */
export function buildCalendarCells(year, month) {
    const startOffset = new Date(year, month - 1, 1).getDay();
    const daysInMonth = new Date(year, month, 0).getDate();
    const prevMonthDays = new Date(year, month - 1, 0).getDate();
    const prev = addCalendarMonths(year, month, -1);
    const next = addCalendarMonths(year, month, 1);
    const cells = [];
    for (let index = 0; index < 42; index++) {
        const dayNum = index - startOffset + 1;
        if (dayNum < 1) {
            const day = prevMonthDays + dayNum;
            cells.push({
                dateValue: `${prev.year}-${pad2(prev.month)}-${pad2(day)}`,
                day,
                inMonth: false
            });
        }
        else if (dayNum > daysInMonth) {
            const day = dayNum - daysInMonth;
            cells.push({
                dateValue: `${next.year}-${pad2(next.month)}-${pad2(day)}`,
                day,
                inMonth: false
            });
        }
        else {
            cells.push({
                dateValue: `${year}-${pad2(month)}-${pad2(dayNum)}`,
                day: dayNum,
                inMonth: true
            });
        }
    }
    return cells;
}
